/** @format */

import React, { useState, useRef, useEffect } from "react";
import { Tag, Select, Form } from "antd";
import { IUsers } from "../../AccountInfoPage.types";

interface IEditablePermissionsProps {
	permissions?: string;
	editedUsers: IUsers[];
	userId: string;
	setTheEditedUsers: (editedUsersList) => void;
	setIfUsersWereEdited: (value) => void;
	changesWereUndone: boolean;
	setUndoChanges: (value) => void;
}

const EditablePermissions: React.FC<IEditablePermissionsProps> = ({
	permissions,
	editedUsers,
	userId,
	setTheEditedUsers,
	setIfUsersWereEdited,
	changesWereUndone,
	setUndoChanges,
}) => {
	const convertPermissionsToArray = (permissions?: string) => {
		return permissions ? permissions.split(",") : [];
	};

	const [changedPermissions, setChangedPermissions] = useState<string[]>(
		convertPermissionsToArray(permissions)
	);
	const [isEditingPermissions, setIsEditingPermission] =
		useState<boolean>(false);
	const [isDropDownVisible, setIsDropDownVisible] = useState<boolean>(false);

	const node: any = useRef();

	useEffect(() => {
		const handleOnClickEditingMode = (e) => {
			if (node.current.contains(e.target)) {
				return;
			}
			if (!isDropDownVisible) {
				setIsEditingPermission(false);
				setIfUsersWereEdited(true);
				const mappedEditedUsers = editedUsers.map((user) => {
					if (user.userId === userId) {
						user = {
							...user,
							permissions: changedPermissions.join(),
							isEdited: true,
						};
					}
					return user;
				});
				setTheEditedUsers(mappedEditedUsers);
			}
		};

		if (isEditingPermissions) {
			document.addEventListener("mousedown", handleOnClickEditingMode);
		} else {
			document.removeEventListener("mousedown", handleOnClickEditingMode);
		}

		return () => {
			document.removeEventListener("mousedown", handleOnClickEditingMode);
		};
	}, [
		isEditingPermissions,
		changedPermissions,
		isDropDownVisible,
		editedUsers,
		permissions,
		setTheEditedUsers,
		userId,
		setIfUsersWereEdited,
	]);

	const handleOnChangeSelect = (value) => {
		setChangedPermissions(value);
	};

	const handleOnDropDownVisible = () => {
		setIsDropDownVisible(true);
	};

	const handleOnDeselect = () => {
		// When user clicks × to remove a permission, auto-blur after short delay
		// This ensures the save logic in handleOnSelect gets triggered
		setTimeout(() => {
			if (document.activeElement) {
				(document.activeElement as HTMLElement).blur();
			}
		}, 100);
	};

	const handleOnSelect = () => {
		setIsDropDownVisible(false);
		// Auto-save and exit editing mode when dropdown closes
		setIsEditingPermission(false);
		setIfUsersWereEdited(true);
		const mappedEditedUsers = editedUsers.map((user) => {
			if (user.userId === userId) {
				user = {
					...user,
					permissions: changedPermissions.join(), // Empty array becomes empty string
					isEdited: true,
				};
			}
			return user;
		});
		setTheEditedUsers(mappedEditedUsers);
	};

	useEffect(() => {
		if (changesWereUndone) {
			setChangedPermissions(convertPermissionsToArray(permissions));
		}
	}, [changesWereUndone, permissions]);

	const renderPermissions = (permissions: string[]) => {
		if (permissions.length > 0) {
			return permissions.map((permission) => {
				if (permission === "billing") {
					return (
						<Tag
							className="editable-cell-value-wrapper"
							color="blue"
							key={permission}>
							{permission}
						</Tag>
					);
				} else if (permission === "admin") {
					return (
						<Tag
							className="editable-cell-value-wrapper"
							color="red"
							key={permission}>
							{permission}
						</Tag>
					);
				} else if (permission === "greg") {
					return (
						<Tag
							className="editable-cell-value-wrapper"
							color="green"
							key={permission}>
							{permission}
						</Tag>
					);
				} else if (permission === "cmp") {
					return (
						<Tag
							className="editable-cell-value-wrapper"
							color="purple"
							key={permission}>
							{permission}
						</Tag>
					);
				}
				return false;
			});
		} else if (permissions.length <= 0) {
			return <Tag>none</Tag>;
		}
	};

	const handleOnClickEditing = () => {
		setIsEditingPermission(true);
		setUndoChanges(false);
	};

	const handleErrorValidation = () => {
		if (changedPermissions.length <= 0) {
			return "error";
		} else if (changedPermissions.length > 0) {
			return "";
		}
	};

	const handleErrorValidationMessage = () => {
		if (changedPermissions.length <= 0) {
			return "Please choose at least one option.";
		} else if (changedPermissions.length > 0) {
			return "";
		}
	};

	return (
		<div
			ref={node}
			onClick={handleOnClickEditing}
			className="editable-cell-value-wrapper">
			{isEditingPermissions ? (
				<Form.Item
					validateStatus={handleErrorValidation()}
					help={handleErrorValidationMessage()}>
					<Select
						style={{ width: "80%" }}
						mode="multiple"
						value={changedPermissions}
						onChange={handleOnChangeSelect}
						onDeselect={handleOnDeselect}
						onBlur={handleOnSelect}
						onDropdownVisibleChange={handleOnDropDownVisible}>
						<Select.Option value="admin">admin</Select.Option>
						<Select.Option value="billing">billing</Select.Option>
						<Select.Option value="greg">greg</Select.Option>
						<Select.Option value="cmp">cmp</Select.Option>
					</Select>
				</Form.Item>
			) : (
				renderPermissions(changedPermissions)
			)}
		</div>
	);
};

export default EditablePermissions;
